"use client";

import { useRef } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { useLoopPhase } from "./useLoopPhase";

/**
 * Looping "job description in, application out" animation.
 * Phases: 0 idle, 1 posting appears, 2 keywords light up, 3-5 Hirecarta works, 6 form fills, 7 submitted (hold).
 */

const GREEN = "#059669";
const NAVY = "#143f80";
const GOLD = "#d69400";
const EASE = [0.16, 1, 0.3, 1] as const;
const HOLDS = [700, 900, 2000, 900, 900, 900, 2600, 4200];

const KEYWORDS = ["budget forecasting", "Excel", "vendor management"];

// Phase at which each step is done. The spinner shows during the phase before.
const STEPS = [
  { label: "Read the posting", doneAt: 3 },
  { label: "Tailor your resume", doneAt: 4 },
  { label: "Write your cover letter", doneAt: 5 },
  { label: "Fill the form", doneAt: 7 },
];

const FIELDS = [
  { label: "Name", value: "Jordan Lee" },
  { label: "Email", value: "jordan.lee@email.com" },
  { label: "Resume", value: "Resume_OpsManager.pdf" },
  { label: "Cover letter", value: "CoverLetter_Acme.pdf" },
  { label: "Authorized to work in the US", value: "Yes" },
];

function Arrow({ on, reduce }: { on: boolean; reduce: boolean }) {
  return (
    <svg viewBox="0 0 48 24" className="mx-auto h-6 w-12 rotate-90 overflow-visible md:rotate-0" aria-hidden>
      <path d="M2 12 L44 12" stroke="#cbd5e1" strokeWidth={3} strokeLinecap="round" fill="none" />
      <motion.path
        d="M2 12 L44 12 M36 5 L44 12 L36 19"
        stroke={GOLD}
        strokeWidth={3.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: on ? 1 : 0 }}
        transition={reduce ? { duration: 0 } : { duration: on ? 0.6 : 0.2, ease: "easeInOut" }}
      />
    </svg>
  );
}

function Spinner() {
  return (
    <motion.span
      className="inline-block h-4 w-4 rounded-full"
      style={{ border: "3px solid #e2e8f0", borderTopColor: GOLD }}
      animate={{ rotate: 360 }}
      transition={{ duration: 0.8, ease: "linear", repeat: Infinity }}
    />
  );
}

function Tick({ on, reduce }: { on: boolean; reduce: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 overflow-visible" aria-hidden>
      <motion.path
        d="M4 12 L10 18 L20 6"
        fill="none"
        stroke={GREEN}
        strokeWidth={4}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: on ? 1 : 0 }}
        transition={reduce ? { duration: 0 } : { duration: 0.3 }}
      />
    </svg>
  );
}

export function LandingExtensionFlow() {
  const ref = useRef<HTMLDivElement>(null);
  const { phase, reduce } = useLoopPhase(ref, HOLDS);
  const working = phase >= 2 && phase < 7;
  const t = (delay = 0, duration = 0.6) => (reduce ? { duration: 0 } : { delay, duration, ease: EASE });

  return (
    <div
      ref={ref}
      className="grid grid-cols-1 items-center gap-4 md:grid-cols-[1fr_3rem_1fr_3rem_1fr]"
      role="img"
      aria-label="A job posting goes into Hirecarta, which reads it, tailors your documents, and fills out the application form."
    >
      {/* 1. Job posting */}
      <motion.div
        className="rounded-2xl bg-white p-6"
        style={{ border: "1px solid rgba(20,63,128,0.08)", boxShadow: "0 4px 24px rgba(20,63,128,0.08)" }}
        animate={{ opacity: phase >= 1 ? 1 : 0.35, y: phase >= 1 ? 0 : 16, x: phase >= 7 && !reduce ? -6 : 0 }}
        transition={t()}
      >
        <p className="mb-3 text-xs font-extrabold uppercase" style={{ color: "#64748b", letterSpacing: "0.16em" }}>
          Job posting
        </p>
        <p className="text-lg font-extrabold" style={{ color: NAVY }}>
          Operations Manager
        </p>
        <p className="mb-4 text-sm" style={{ color: "#64748b" }}>
          Acme Co. · Remote
        </p>
        <div className="space-y-2.5 text-sm leading-relaxed" style={{ color: "#334155" }}>
          <p>
            Own{" "}
            <Mark on={phase >= 2} delay={0.1} reduce={reduce}>
              {KEYWORDS[0]}
            </Mark>{" "}
            and monthly reporting in{" "}
            <Mark on={phase >= 2} delay={0.5} reduce={reduce}>
              {KEYWORDS[1]}
            </Mark>
            .
          </p>
          <p>
            Lead{" "}
            <Mark on={phase >= 2} delay={0.9} reduce={reduce}>
              {KEYWORDS[2]}
            </Mark>{" "}
            across 12 suppliers.
          </p>
          <div className="h-2 w-5/6 rounded-full bg-slate-200" />
          <div className="h-2 w-2/3 rounded-full bg-slate-200" />
        </div>
      </motion.div>

      <Arrow on={phase >= 2} reduce={reduce} />

      {/* 2. Hirecarta */}
      <div
        className="flex flex-col items-center rounded-2xl p-6"
        style={{ background: "#143f80", boxShadow: "0 8px 36px rgba(20,63,128,0.28)" }}
      >
        <div className="relative mb-5 flex h-20 w-20 items-center justify-center">
          <motion.span
            className="absolute inset-0 rounded-full"
            style={{ border: `3px solid ${GOLD}` }}
            animate={working && !reduce ? { scale: [1, 1.5], opacity: [0.7, 0] } : { scale: 1, opacity: 0 }}
            transition={working && !reduce ? { duration: 1.4, ease: "easeOut", repeat: Infinity } : { duration: 0 }}
          />
          <motion.span
            className="absolute inset-0 rounded-full"
            style={{ border: "3px dashed rgba(255,255,255,0.35)" }}
            animate={working && !reduce ? { rotate: 360 } : { rotate: 0 }}
            transition={working && !reduce ? { duration: 6, ease: "linear", repeat: Infinity } : { duration: 0 }}
          />
          <Image src="/images/rounded-logo.png" alt="" width={56} height={56} className="relative h-14 w-14 rounded-full" />
        </div>
        <ul className="w-full space-y-3">
          {STEPS.map((s) => {
            const done = phase >= s.doneAt;
            const active = !done && phase === s.doneAt - 1 && phase >= 2;
            return (
              <li key={s.label} className="flex items-center gap-3 text-sm font-bold" style={{ color: done || active ? "#ffffff" : "rgba(255,255,255,0.4)" }}>
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white">
                  {done ? <Tick on reduce={reduce} /> : active ? <Spinner /> : null}
                </span>
                {s.label}
              </li>
            );
          })}
        </ul>
      </div>

      <Arrow on={phase >= 6} reduce={reduce} />

      {/* 3. Application form */}
      <motion.div
        className="relative rounded-2xl bg-white p-6"
        style={{ border: "1px solid rgba(20,63,128,0.08)", boxShadow: "0 4px 24px rgba(20,63,128,0.08)" }}
        animate={{ opacity: phase >= 1 ? 1 : 0.35, y: phase >= 1 ? 0 : 16 }}
        transition={t(0.15)}
      >
        <p className="mb-3 text-xs font-extrabold uppercase" style={{ color: "#64748b", letterSpacing: "0.16em" }}>
          Application
        </p>
        <div className="space-y-2.5">
          {FIELDS.map((f, i) => (
            <div key={f.label}>
              <p className="text-xs font-semibold" style={{ color: "#64748b" }}>
                {f.label}
              </p>
              <div className="relative h-8 rounded-md bg-slate-100">
                <motion.span
                  className="absolute inset-0 flex items-center whitespace-nowrap rounded-md px-2 text-sm font-bold"
                  style={{ color: NAVY, background: "rgba(5,150,105,0.10)", border: "1px solid rgba(5,150,105,0.35)" }}
                  initial={{ clipPath: "inset(0 100% 0 0)" }}
                  animate={{ clipPath: phase >= 6 ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)" }}
                  transition={t(i * 0.32, 0.45)}
                >
                  {f.value}
                </motion.span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <motion.span
            className="rounded-full px-5 py-1.5 text-sm font-extrabold text-white"
            style={{ background: GREEN }}
            animate={phase >= 7 && !reduce ? { scale: [1, 0.92, 1] } : { scale: 1 }}
            transition={phase >= 7 && !reduce ? { delay: 0.2, duration: 0.3 } : { duration: 0 }}
          >
            {phase >= 7 ? "Submitted" : "Submit"}
          </motion.span>
        </div>
        <motion.span
          className="pointer-events-none absolute -right-2 -top-3 rounded-full px-3 py-1 text-xs font-extrabold text-white"
          style={{ background: GOLD, boxShadow: "0 2px 12px rgba(214,148,0,0.4)" }}
          initial={{ opacity: 0, scale: 0.5, rotate: 10 }}
          animate={phase >= 7 ? { opacity: 1, scale: 1, rotate: -6 } : { opacity: 0, scale: 0.5, rotate: 10 }}
          transition={reduce ? { duration: 0 } : { delay: 0.7, type: "spring", stiffness: 320, damping: 14 }}
        >
          Logged to tracker
        </motion.span>
      </motion.div>
    </div>
  );
}

function Mark({
  on,
  delay,
  reduce,
  children,
}: {
  on: boolean;
  delay: number;
  reduce: boolean;
  children: string;
}) {
  return (
    <motion.mark
      className="rounded px-1 font-bold"
      style={{ color: NAVY }}
      animate={{ backgroundColor: on ? "rgba(214,148,0,0.35)" : "rgba(214,148,0,0)" }}
      transition={reduce ? { duration: 0 } : { delay: on ? delay : 0, duration: 0.35 }}
    >
      {children}
    </motion.mark>
  );
}
